import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useCheckInContext } from "./CheckInDataContext";
import { describeBadgeError } from "./badgesCrud";
import { isIsoDate, todayIsoDate } from "./attendanceCrud";

/**
 * One branch, one day, one fetch — shared by the Attendance and Competitions
 * tabs, which differ only in which endpoint they call and how they draw the
 * answer.
 *
 * THE DAY LIVES IN THE URL. `?date=YYYY-MM-DD`, read the same way
 * `BadgePrintList` reads `?batch_id=` — so a particular day can be linked,
 * bookmarked and sent to someone else, and the back button steps back through
 * the days you looked at. A malformed or missing `date` is ignored outright
 * and the screen opens on today, exactly as an unparseable `batch_id` falls
 * back to the empty picker rather than crashing or requesting date `NaN`.
 *
 * Three other things this deliberately gets right:
 *
 *  1. It does not fetch until `authToken` is in the store. `setupAxios` is
 *     commented out at `src/index.js:48`, so `axios.defaults` only gains the
 *     `x-auth-token` header as a side effect of `getUserByToken()` in the
 *     rehydrate saga — which resolves *after* mount. Anything that fetches on
 *     mount races that and comes back 401, which staff read as "your session
 *     expired" on a perfectly good session.
 *
 *  2. It distinguishes "not asked yet" from "asked, and the answer was none".
 *     Without `hasLoaded`, an empty array on first paint is indistinguishable
 *     from a day with no sessions.
 *
 *  3. It never leaves a spinner running over a refusal. Every path that
 *     declines to fetch clears `isLoading` on the way out — see the note on
 *     `stopLoading` below.
 *
 * Branch comes from `auth.branches.currentBranch` by way of CheckInDataContext
 * — the shared subheader picker. There is deliberately no second branch
 * selector anywhere in Check-in.
 */
export function useKioskDay(fetcher, forbiddenMessage, fallbackMessage) {
  const { BRANCH } = useCheckInContext();
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);
  const branchId = BRANCH.branchId;
  const branchReady = Boolean(branchId) && branchId !== -1;

  const history = useHistory();
  const location = useLocation();

  const urlDate = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("date");
    return raw !== null && isIsoDate(raw) ? raw : null;
  }, [location.search]);

  /*
    A date the user has typed that is not yet (or never) a real date — in
    practice the native picker being cleared, which hands back "".

    This is held locally rather than pushed to the URL on purpose. A malformed
    value arriving *in a link* is someone else's typo and is ignored silently
    (today is shown); a box the user just emptied *themselves* is a deliberate
    act mid-edit, and answering it with a different day's data would be the
    screen overruling them. So that one shows the refusal instead — and, per
    (3) above, shows it without a spinner and without a request in flight.
  */
  const [draftDate, setDraftDate] = useState(null);

  // Navigating — the back button, or a fresh link — always wins over a
  // half-typed date left in the box, otherwise going back from a cleared
  // picker would keep showing the refusal instead of the day you went back to.
  useEffect(() => {
    setDraftDate(null);
  }, [location.search]);

  const date = draftDate !== null ? draftDate : urlDate || todayIsoDate();
  const dateIsValid = isIsoDate(date);

  const setDate = useCallback(
    (next) => {
      if (isIsoDate(next)) {
        setDraftDate(null);
        history.push(`${location.pathname}?date=${next}`);
      } else {
        setDraftDate(next);
      }
    },
    [history, location.pathname]
  );

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);
  // Bumped by refresh(); the effect below is the only thing that fetches, so
  // a manual reload and a changed date travel down exactly the same path.
  const [reloadToken, setReloadToken] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    /*
      Clearing `isLoading` here is load-bearing, and its absence was a real
      bug: an in-flight fetch that gets cancelled (the date changed under it)
      skips its own `finally`, because that is guarded on `!cancelled`. If the
      effect run that replaces it then declines to fetch — invalid date, no
      branch, no token — nothing ever turns the spinner off, and the screen
      showed "Pick a valid date." and "Loading…" side by side: a refusal and a
      promise to answer, at the same time.
    */
    const stopLoading = () => {
      setRows([]);
      setHasLoaded(false);
      setIsLoading(false);
    };

    if (!branchReady || !authToken) {
      // Not an error state — the screen says which of the two it is waiting on
      // in words. Clearing keeps one branch's roster off another's heading.
      stopLoading();
      setErrorMessage("");
      return undefined;
    }
    if (!dateIsValid) {
      stopLoading();
      setErrorMessage("Pick a valid date.");
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const result = await fetcherRef.current(branchId, date, authToken);
        if (cancelled) return;
        setRows(result);
        setHasLoaded(true);
      } catch (error) {
        if (cancelled) return;
        setRows([]);
        // Nothing is shown as loaded on a failure: a failed fetch must never
        // be able to paint an empty table that reads as "nobody was here".
        setHasLoaded(false);
        setErrorMessage(
          describeBadgeError(error, fallbackMessage, forbiddenMessage)
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    branchId,
    branchReady,
    authToken,
    date,
    dateIsValid,
    reloadToken,
    forbiddenMessage,
    fallbackMessage,
  ]);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);

  return {
    date,
    setDate,
    dateIsValid,
    rows,
    isLoading,
    errorMessage,
    hasLoaded,
    refresh,
    branchId,
    branchReady,
    authToken,
    currentBranch: BRANCH.currentBranch,
  };
}
